import { FC, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { ExpandMore } from "@mui/icons-material"
import {
  usePhoneLoginMutation,
  useClientLoginCodeMutation,
  useClientLoginPasswordMutation,
  useSessionImportMutation,
  useTdataImportMutation,
} from "../../store"
import { SecretField } from "@/core/components"
import { LoginStep, nextLoginStep } from "./loginMachine"
import {
  validateTdataFiles,
  REQUIRED_TDATA_FILES,
  MAX_TDATA_FILES,
} from "./tdataUtils"
import { getChainerError } from "./chainerError"
import { blockBackdropClose } from "../common/dialogUtils"
import { buildTwoFaImportFields, appendTwoFaFields } from "./twoFaImportFields"

type Branch = "phone" | "session" | "tdata"
type PhoneStep = LoginStep // "idle" | "code" | "password" | "done"

interface DeviceOverrides {
  name: string
  apiId: string
  apiHash: string
  deviceModel: string
  systemVersion: string
  appVersion: string
  langCode: string
  langPack: string
  proxyId: string
}

const emptyOverrides: DeviceOverrides = {
  name: "",
  apiId: "",
  apiHash: "",
  deviceModel: "",
  systemVersion: "",
  appVersion: "",
  langCode: "",
  langPack: "",
  proxyId: "",
}

/** Сборка opt-in device-override'ов: пустые поля не отправляются (бэк подставит TD-дефолты). */
const buildOverrides = (o: DeviceOverrides) => {
  const res: Record<string, string | number> = {}
  if (o.name.trim()) res.name = o.name.trim()
  if (o.apiId.trim()) res.apiId = Number(o.apiId)
  if (o.apiHash.trim()) res.apiHash = o.apiHash.trim()
  if (o.deviceModel.trim()) res.deviceModel = o.deviceModel.trim()
  if (o.systemVersion.trim()) res.systemVersion = o.systemVersion.trim()
  if (o.appVersion.trim()) res.appVersion = o.appVersion.trim()
  if (o.langCode.trim()) res.langCode = o.langCode.trim()
  if (o.langPack.trim()) res.langPack = o.langPack.trim()
  if (o.proxyId.trim()) res.proxyId = Number(o.proxyId)
  return res
}

interface IProps {
  onClose: () => void
}

/**
 * Единый визард «Добавить сессию» (one-shot создание клиента).
 * 3 ветки: Телефон (интерактивный phone-login → code → password),
 * String-session (one-shot), tdata-папка (one-shot multipart).
 * Общая свёрнутая секция расширенных настроек (device override'ы) — по умолчанию пусто.
 */
export const ClientCreateDialog: FC<IProps> = ({ onClose }) => {
  const [branch, setBranch] = useState<Branch>("phone")
  const [done, setDone] = useState(false)
  const [doneName, setDoneName] = useState("")

  // phone-ветка
  const [phoneStep, setPhoneStep] = useState<PhoneStep>("idle")
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [password, setPassword] = useState("")
  const [clientId, setClientId] = useState<string | null>(null)
  const [sentCodeType, setSentCodeType] = useState<string | null>(null)

  // session-ветка
  const [session, setSession] = useState("")
  const [force, setForce] = useState(false)

  // tdata-ветка
  const [tdataFiles, setTdataFiles] = useState<File[]>([])

  // 2FA для session и tdata веток
  const [twoFaPassword, setTwoFaPassword] = useState("")
  const [twoFaPhoneDigits, setTwoFaPhoneDigits] = useState("")

  // общее
  const [error, setError] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [overrides, setOverrides] = useState<DeviceOverrides>(emptyOverrides)

  const [phoneLogin, phoneState] = usePhoneLoginMutation()
  const [sendCode, codeState] = useClientLoginCodeMutation()
  const [sendPassword, passwordState] = useClientLoginPasswordMutation()
  const [sessionImport, sessionState] = useSessionImportMutation()
  const [tdataImport, tdataState] = useTdataImportMutation()

  const loading =
    phoneState.isLoading ||
    codeState.isLoading ||
    passwordState.isLoading ||
    sessionState.isLoading ||
    tdataState.isLoading

  const updateOverride = (key: keyof DeviceOverrides, value: string) =>
    setOverrides((prev) => ({ ...prev, [key]: value }))

  const resetBranchState = () => {
    setError(null)
    setPhoneStep("idle")
    setClientId(null)
    setSentCodeType(null)
    setDone(false)
    setTwoFaPassword("")
    setTwoFaPhoneDigits("")
  }

  const handleBranchChange = (b: Branch) => {
    setBranch(b)
    resetBranchState()
  }

  const handlePhoneLogin = async () => {
    setError(null)
    if (!phone.trim()) {
      setError("Введите номер телефона")
      return
    }
    try {
      const res = await phoneLogin({
        body: { phone: phone.trim(), ...buildOverrides(overrides) },
      }).unwrap()
      setClientId(res.clientId)
      setSentCodeType(res.sentCodeType ?? null)
      setPhoneStep(nextLoginStep("idle", { kind: "started" }))
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  const handleSendCode = async () => {
    if (!clientId) return
    setError(null)
    try {
      const res = await sendCode({ id: clientId, body: { code } }).unwrap()
      const next = nextLoginStep("code", {
        kind: "code",
        needsPassword: res.needsPassword,
      })
      if (next === "done") {
        setDone(true)
        setDoneName(overrides.name.trim() || phone.trim())
      } else {
        setPhoneStep(next)
      }
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  const handleSendPassword = async () => {
    if (!clientId) return
    setError(null)
    try {
      const res = await sendPassword({
        id: clientId,
        body: { password },
      }).unwrap()
      const next = nextLoginStep("password", {
        kind: "password",
        needsPassword: res.needsPassword,
      })
      if (next === "done") {
        setDone(true)
        setDoneName(overrides.name.trim() || phone.trim())
      } else {
        setPhoneStep(next)
      }
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  const handleSessionImport = async () => {
    setError(null)
    if (!session.trim()) {
      setError("Введите строку сессии")
      return
    }
    try {
      const res = await sessionImport({
        body: {
          session: session.trim(),
          force,
          ...buildTwoFaImportFields(twoFaPassword, twoFaPhoneDigits),
          ...buildOverrides(overrides),
        },
      }).unwrap()
      setDone(true)
      setDoneName(res.name || overrides.name.trim() || "сессия")
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  const handleTdataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    const err = validateTdataFiles(files)
    setError(err)
    setTdataFiles(err ? [] : files)
  }

  const handleTdataImport = async () => {
    setError(null)
    const err = validateTdataFiles(tdataFiles)
    if (err) {
      setError(err)
      return
    }
    const formData = new FormData()
    tdataFiles.forEach((f) => formData.append("files", f))
    const o = buildOverrides(overrides)
    Object.entries(o).forEach(([k, v]) => formData.append(k, String(v)))
    appendTwoFaFields(
      formData,
      buildTwoFaImportFields(twoFaPassword, twoFaPhoneDigits),
    )
    try {
      const res = await tdataImport({ body: formData }).unwrap()
      setDone(true)
      setDoneName(res.name || overrides.name.trim() || "tdata")
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  // Блок 2FA для session и tdata веток (пароль ИЛИ число цифр телефона)
  const renderTwoFaBlock = () => (
    <Stack
      spacing={1}
      sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
    >
      <Typography variant="subtitle2">2FA (опционально)</Typography>
      <Typography variant="caption" color="text.secondary">
        Если пароль — последние N цифр номера телефона, оставьте поле пароля
        пустым и укажите N.
      </Typography>
      <TextField
        label="Текущий 2FA-пароль (если известен и не телефонный)"
        type="password"
        value={twoFaPassword}
        onChange={(e) => setTwoFaPassword(e.target.value)}
        fullWidth
        autoComplete="new-password"
      />
      <TextField
        label="Цифр телефона в пароле (1–15)"
        type="number"
        placeholder="4"
        value={twoFaPhoneDigits}
        onChange={(e) => setTwoFaPhoneDigits(e.target.value)}
        inputProps={{ min: 1, max: 15 }}
        fullWidth
      />
    </Stack>
  )

  // Параметры primary-кнопки в зависимости от ветки/шага
  let primaryLabel = ""
  let primaryHandler: () => void = () => {}
  let primaryDisabled = true
  if (!done) {
    if (branch === "phone") {
      if (phoneStep === "idle") {
        primaryLabel = "Войти"
        primaryHandler = handlePhoneLogin
        primaryDisabled = loading || !phone.trim()
      } else if (phoneStep === "code") {
        primaryLabel = "Отправить код"
        primaryHandler = handleSendCode
        primaryDisabled = loading || !code
      } else if (phoneStep === "password") {
        primaryLabel = "Подтвердить"
        primaryHandler = handleSendPassword
        primaryDisabled = loading || !password
      }
    } else if (branch === "session") {
      primaryLabel = "Импортировать"
      primaryHandler = handleSessionImport
      primaryDisabled = loading || !session.trim()
    } else if (branch === "tdata") {
      primaryLabel = "Импортировать tdata"
      primaryHandler = handleTdataImport
      primaryDisabled = loading || tdataFiles.length === 0
    }
  }

  return (
    <Dialog open onClose={blockBackdropClose(onClose)} maxWidth="sm" fullWidth>
      <DialogTitle>{done ? "Сессия добавлена" : "Добавить сессию"}</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success">
            Клиент «{doneName}» создан и авторизован.
          </Alert>
        ) : (
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}

            <RadioGroup
              value={branch}
              onChange={(_, v) => handleBranchChange(v as Branch)}
            >
              <FormControlLabel
                value="phone"
                control={<Radio />}
                label="Телефон (код из Telegram)"
              />
              <FormControlLabel
                value="session"
                control={<Radio />}
                label="String-session"
              />
              <FormControlLabel
                value="tdata"
                control={<Radio />}
                label="tdata (папка Telegram Desktop)"
              />
            </RadioGroup>

            <Divider />

            {branch === "phone" && (
              <>
                {phoneStep === "idle" && (
                  <TextField
                    label="Номер телефона (E.164 без +)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="79991234567"
                    fullWidth
                    autoFocus
                  />
                )}
                {phoneStep === "code" && (
                  <Stack spacing={1.5}>
                    {sentCodeType === "app" ? (
                      <Alert severity="info">
                        Код подтверждения отправлен в приложение Telegram (чат
                        777000).
                      </Alert>
                    ) : sentCodeType ? (
                      <Alert severity="warning">
                        Код отправлен через {sentCodeType.toUpperCase()} (не в
                        приложение Telegram). Проверьте SMS или входящий звонок.
                      </Alert>
                    ) : null}
                    <TextField
                      label="Код из Telegram"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      fullWidth
                      autoFocus
                    />
                  </Stack>
                )}
                {phoneStep === "password" && (
                  <TextField
                    label="Пароль 2FA"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    autoFocus
                  />
                )}
              </>
            )}

            {branch === "session" && (
              <>
                <TextField
                  label="Строка сессии"
                  value={session}
                  onChange={(e) => setSession(e.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  inputProps={{ style: { fontFamily: "monospace" } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={force}
                      onChange={(e) => setForce(e.target.checked)}
                    />
                  }
                  label="Принудительно (перезаписать существующую)"
                />
                {renderTwoFaBlock()}
              </>
            )}

            {branch === "tdata" && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Выберите файлы из папки tdata (без архива/zip). Обязательные:{" "}
                  {REQUIRED_TDATA_FILES.join(", ")}. До {MAX_TDATA_FILES}{" "}
                  файлов.
                </Typography>
                <input
                  type="file"
                  multiple
                  onChange={handleTdataChange}
                  aria-label="Файлы tdata"
                />
                {tdataFiles.length > 0 && (
                  <Typography variant="body2">
                    Выбрано файлов: {tdataFiles.length}
                  </Typography>
                )}
                {renderTwoFaBlock()}
              </Stack>
            )}

            <Accordion
              expanded={advancedOpen}
              onChange={(_, v) => setAdvancedOpen(v)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>
                  Расширенные настройки (device override'ы)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    Поля по умолчанию пусты — бэк подставит TD-дефолты
                    (apiId:2040, device Desktop/Windows 10/4.16.0/ru/ru).
                    Заполняйте только для переопределения.
                  </Typography>
                  <TextField
                    label="Имя сессии"
                    value={overrides.name}
                    onChange={(e) => updateOverride("name", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="API ID"
                    type="number"
                    value={overrides.apiId}
                    onChange={(e) => updateOverride("apiId", e.target.value)}
                    fullWidth
                  />
                  <SecretField
                    label="apiHash"
                    value={overrides.apiHash}
                    onChange={(v) => updateOverride("apiHash", v)}
                    fullWidth
                  />
                  <TextField
                    label="Модель устройства"
                    value={overrides.deviceModel}
                    onChange={(e) =>
                      updateOverride("deviceModel", e.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label="Версия системы"
                    value={overrides.systemVersion}
                    onChange={(e) =>
                      updateOverride("systemVersion", e.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label="Версия приложения"
                    value={overrides.appVersion}
                    onChange={(e) =>
                      updateOverride("appVersion", e.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    label="Код языка"
                    value={overrides.langCode}
                    onChange={(e) => updateOverride("langCode", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Lang pack"
                    value={overrides.langPack}
                    onChange={(e) => updateOverride("langPack", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Proxy ID"
                    type="number"
                    value={overrides.proxyId}
                    onChange={(e) => updateOverride("proxyId", e.target.value)}
                    fullWidth
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? "Закрыть" : "Отмена"}</Button>
        {!done && (
          <Button
            variant="contained"
            onClick={primaryHandler}
            disabled={primaryDisabled}
          >
            {primaryLabel}
            {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
